/**
 * DatePickerModal — Calendrier React Native (zero dependance)
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Modal, View, Text, TouchableOpacity, StyleSheet,
  Animated, Dimensions, Platform,
} from 'react-native';

const { height: SCREEN_H } = Dimensions.get('window');
const MONTHS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin',
                   'Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
const DAYS_FR = ['Lu','Ma','Me','Je','Ve','Sa','Di'];

function isoToDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0,0,0,0);
  return dt;
}
function dateToIso(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}
function sameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
}

export default function DatePickerModal({ visible, value, minDate, onConfirm, onCancel, title }) {
  const today = useMemo(() => { const d=new Date(); d.setHours(0,0,0,0); return d; }, []);
  const minD  = useMemo(() => isoToDate(minDate), [minDate]);

  const [viewYear,  setViewYear]  = useState((isoToDate(value)||today).getFullYear());
  const [viewMonth, setViewMonth] = useState((isoToDate(value)||today).getMonth());
  const [selected,  setSelected]  = useState(null);
  // Utiliser useRef pour slideAnim (vrai instance stable, pas re-créée)
  const slideAnim = React.useRef(new Animated.Value(SCREEN_H)).current;

  // Reinitialiser a chaque ouverture
  useEffect(() => {
    if (!visible) { slideAnim.setValue(SCREEN_H); return; }
    const init = isoToDate(value) || today;
    setViewYear(init.getFullYear());
    setViewMonth(init.getMonth());
    setSelected(isoToDate(value));
    // Animation plus légère (timing au lieu de spring qui peut bouncer)
    Animated.timing(slideAnim, { toValue:0, duration:200, useNativeDriver:true }).start();
  }, [visible]);

  const closeDown = useCallback((cb) => {
    Animated.timing(slideAnim, { toValue:SCREEN_H, duration:180, useNativeDriver:true })
      .start(() => cb && cb());
  }, [slideAnim]);

  const grid = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    let off = first.getDay() - 1; if (off < 0) off = 6;
    const dim = new Date(viewYear, viewMonth+1, 0).getDate();
    const cells = Array(off).fill(null);
    for (let d=1; d<=dim; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const prevMonth = () => { if(viewMonth===0){setViewYear(y=>y-1);setViewMonth(11);}else setViewMonth(m=>m-1); };
  const nextMonth = () => { if(viewMonth===11){setViewYear(y=>y+1);setViewMonth(0);}else setViewMonth(m=>m+1); };

  const selectDay = (day) => {
    if (!day) return;
    const d = new Date(viewYear, viewMonth, day); d.setHours(0,0,0,0);
    if (minD && d < minD) return;
    setSelected(d);
  };

  const cellSt = (day) => {
    if (!day) return 'empty';
    const d = new Date(viewYear, viewMonth, day); d.setHours(0,0,0,0);
    if (minD && d < minD) return 'disabled';
    if (selected && sameDay(d, selected)) return 'selected';
    if (sameDay(d, today)) return 'today';
    return 'normal';
  };

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={() => closeDown(onCancel)}>
      <View style={s.backdrop}>
        <TouchableOpacity style={StyleSheet.absoluteFill} onPress={() => closeDown(onCancel)} activeOpacity={1} />
        <Animated.View style={[s.sheet, { transform:[{translateY:slideAnim}] }]}>

          <View style={s.handle} />

          {/* Header */}
          <View style={s.header}>
            <TouchableOpacity onPress={() => closeDown(onCancel)} hitSlop={{top:10,bottom:10,left:10,right:10}}>
              <Text style={s.cancelTxt}>Annuler</Text>
            </TouchableOpacity>
            <Text style={s.title}>{title || 'Choisir une date'}</Text>
            <TouchableOpacity
              onPress={() => selected && closeDown(() => onConfirm(dateToIso(selected)))}
              style={[s.confirmBtn, !selected && s.confirmBtnOff]}
              disabled={!selected}>
              <Text style={[s.confirmTxt, !selected && s.confirmTxtOff]}>OK</Text>
            </TouchableOpacity>
          </View>

          {/* Date selectionnee */}
          <View style={s.selBar}>
            <Text style={selected ? s.selTxt : s.selPlaceholder}>
              {selected
                ? `${selected.getDate()} ${MONTHS_FR[selected.getMonth()]} ${selected.getFullYear()}`
                : 'Appuyez sur un jour'}
            </Text>
          </View>

          {/* Navigation */}
          <View style={s.nav}>
            <TouchableOpacity onPress={prevMonth} style={s.navBtn}>
              <Text style={s.navArrow}>{'<'}</Text>
            </TouchableOpacity>
            <View style={s.navCenter}>
              <Text style={s.navMonth}>{MONTHS_FR[viewMonth]}</Text>
              <Text style={s.navYear}>{viewYear}</Text>
            </View>
            <TouchableOpacity onPress={nextMonth} style={s.navBtn}>
              <Text style={s.navArrow}>{'>'}</Text>
            </TouchableOpacity>
          </View>

          {/* Jours semaine */}
          <View style={s.weekRow}>
            {DAYS_FR.map(d => <Text key={d} style={s.weekDay}>{d}</Text>)}
          </View>

          {/* Grille */}
          <View style={s.grid}>
            {grid.map((day, i) => {
              const st = cellSt(day);
              return (
                <TouchableOpacity key={i}
                  style={[s.cell, st==='disabled'&&s.cellDis]}
                  onPress={() => selectDay(day)}
                  disabled={!day || st==='disabled'}
                  activeOpacity={0.55}>
                  <View style={[
                    s.cellInner,
                    st==='selected'&&s.cellSel,
                    st==='today'&&s.cellToday,
                  ]}>
                    {day ? (
                      <Text style={[
                        s.cellTxt,
                        st==='selected'&&s.cellTxtSel,
                        st==='today'&&s.cellTxtToday,
                        st==='disabled'&&s.cellTxtDis,
                      ]}>
                        {day}
                      </Text>
                    ) : null}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Raccourcis */}
          <View style={s.shortcuts}>
            {[{l:'Demain',d:1},{l:'1 sem.',d:7},{l:'2 sem.',d:14},{l:'1 mois',d:30},{l:'3 mois',d:90},{l:'6 mois',d:180}].map(sc => {
              const dt = new Date(today); dt.setDate(dt.getDate()+sc.d);
              const off = !!(minD && dt < minD);
              return (
                <TouchableOpacity key={sc.l} style={[s.shortcut, off&&s.shortcutOff]}
                  onPress={() => { if(!off){setSelected(dt);setViewYear(dt.getFullYear());setViewMonth(dt.getMonth());} }}
                  disabled={off}>
                  <Text style={[s.shortcutTxt, off&&s.shortcutTxtOff]}>{sc.l}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

        </Animated.View>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  backdrop:       { flex:1, justifyContent:'flex-end', backgroundColor:'transparent' },
  sheet:          { backgroundColor:'#fff', borderTopLeftRadius:24, borderTopRightRadius:24, paddingBottom: Platform.OS==='ios' ? 34 : 20 },
  handle:         { width:40, height:4, borderRadius:2, backgroundColor:'#E0E0E0', alignSelf:'center', marginTop:10, marginBottom:4 },
  header:         { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#F0F0F0' },
  title:          { fontSize:15, fontWeight:'600', color:'#111', flex:1, textAlign:'center' },
  cancelTxt:      { fontSize:14, color:'#888', minWidth:60 },
  confirmBtn:     { backgroundColor:'#111', paddingHorizontal:18, paddingVertical:8, borderRadius:20, minWidth:60, alignItems:'center' },
  confirmBtnOff:  { backgroundColor:'#EFEFEF' },
  confirmTxt:     { fontSize:13, fontWeight:'700', color:'#fff' },
  confirmTxtOff:  { color:'#CCC' },
  selBar:         { alignItems:'center', justifyContent:'center', paddingVertical:14, backgroundColor:'#F8F8F8' },
  selTxt:         { fontSize:22, fontWeight:'300', color:'#111', letterSpacing:0.3, textAlign:'center', width:'100%' },
  selPlaceholder: { fontSize:14, color:'#BDBDBD', textAlign:'center', width:'100%' },
  nav:            { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:12, paddingVertical:10 },
  navBtn:         { width:40, height:40, alignItems:'center', justifyContent:'center', borderRadius:20, backgroundColor:'#F2F2F2' },
  navArrow:       { fontSize:20, color:'#111', fontWeight:'600' },
  navCenter:      { alignItems:'center' },
  navMonth:       { fontSize:17, fontWeight:'600', color:'#111' },
  navYear:        { fontSize:12, color:'#999', marginTop:1 },
  weekRow:        { flexDirection:'row', paddingHorizontal:8, paddingBottom:6 },
  weekDay:        { width:`${100/7}%`, textAlign:'center', fontSize:11, fontWeight:'600', color:'#BDBDBD', letterSpacing:0.8 },
  grid:           { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:8 },
  cell:           { width:`${100/7}%`, height:44, alignItems:'center', justifyContent:'center' },
  cellInner:      { width:36, height:36, alignItems:'center', justifyContent:'center', borderRadius:18 },
  cellSel:        { backgroundColor:'#111' },
  cellToday:      { borderWidth:2, borderColor:'#111' },
  cellDis:        { opacity:0.2 },
  cellTxt:        { fontSize:14, color:'#111', lineHeight:36, textAlign:'center' },
  cellTxtSel:     { color:'#fff', fontWeight:'700' },
  cellTxtToday:   { fontWeight:'700', color:'#111' },
  cellTxtDis:     { color:'#CCC' },
  shortcuts:      { flexDirection:'row', flexWrap:'wrap', gap:8, paddingHorizontal:16, paddingTop:10 },
  shortcut:       { paddingHorizontal:14, paddingVertical:7, borderRadius:20, borderWidth:1, borderColor:'#E5E5E5', backgroundColor:'#FAFAFA' },
  shortcutOff:    { opacity:0.3 },
  shortcutTxt:    { fontSize:12, color:'#444' },
  shortcutTxtOff: { color:'#CCC' },
});
